/**
 * Persistent inbound voice receiver (FE-INB-1).
 *
 * Owns the ONE Twilio Device whose job is to stay registered as
 * `agent-{userId}` and deliver `incoming` calls — the browser-side half of the
 * backend's `<Dial><Client>agent-{userId}</Client></Dial>` ring. It is
 * deliberately separate from twilio.context's ephemeral outbound Device:
 * incoming dispatch only follows REGISTERED devices, so the two never clash.
 *
 * Lifecycle rules:
 *   - `recycle()` after every finished inbound call: an idle Device's
 *     AudioHelper can keep holding the mic (the same bug class the outbound
 *     flow avoids by destroying its Device per call), so the receiver is
 *     rebuilt fresh instead of reused. Deferred a tick because it is invoked
 *     from within the dying call's own event dispatch.
 *   - Registration failures self-heal with capped exponential backoff, and a
 *     browser `online` event short-circuits the wait. Nothing retries while a
 *     call is live on the device, and nothing retries after `destroy()`.
 *   - The token refreshes in place via `tokenWillExpire`; a failed refresh is
 *     left to the unregistered/error path, which rebuilds with a new token.
 */
import { Device, Call } from "@twilio/voice-sdk";
import * as Sentry from "@sentry/react";
import { fetchAgentVoiceToken } from "@/services/call-center/twilio.token.service";

const LOG = "[TwilioReceiver]";

const BASE_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 30_000;

export type VoiceReceiver = {
  /** Tear down the current Device and register a fresh one (post-call mic release). */
  recycle: () => void;
  /** Stop receiving for good (logout, line lost). Idempotent. */
  destroy: () => void;
};

export type VoiceReceiverOptions = {
  /**
   * A call to the agent's line is ringing. The provider decides what happens
   * (collision guard, banner, accept/reject) — the receiver only delivers it.
   * `device` is the Device the call lives on, so an accepted call can attach
   * audio processors to the right AudioHelper.
   */
  onIncoming: (call: Call, device: Device) => void;
};

const breadcrumb = (message: string, data?: Record<string, unknown>) =>
  Sentry.addBreadcrumb({ category: "twilio.receiver", level: "info", message, data });

export function createVoiceReceiver({ onIncoming }: VoiceReceiverOptions): VoiceReceiver {
  let device: Device | null = null;
  let destroyed = false;
  let retryTimer: number | null = null;
  let attempt = 0;
  // Bumped by every (re)start; async continuations bail when they lost the
  // race to a newer start (or to destroy), so two starts never stack devices.
  let generation = 0;

  const clearRetry = () => {
    if (retryTimer !== null) {
      window.clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const teardownDevice = () => {
    const stale = device;
    device = null;
    // destroy() stops the AudioHelper's streams and unregisters — its own
    // "unregistered" event is ignored because `device` no longer points at it.
    try { stale?.destroy(); } catch { /* noop */ }
  };

  const scheduleRetry = (why: string) => {
    if (destroyed || retryTimer !== null) return;
    attempt += 1;
    const delay =
      Math.min(MAX_RETRY_DELAY_MS, BASE_RETRY_DELAY_MS * 2 ** Math.min(attempt, 5)) +
      Math.round(Math.random() * 500);
    breadcrumb(`Receiver retry scheduled (${why})`, { attempt, delay });
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      void start();
    }, delay);
  };

  const start = async () => {
    if (destroyed) return;
    const gen = ++generation;
    clearRetry();
    teardownDevice();
    try {
      const { token, identity } = await fetchAgentVoiceToken();
      if (destroyed || gen !== generation) return;

      // Same audio tuning as the outbound Device — an accepted inbound call
      // lives on THIS device's media stack.
      const dev = new Device(token, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        maxAverageBitrate: 16000,
        edge: "roaming",
        enableImprovedSignalingErrorPrecision: true,
      });
      device = dev;
      dev.audio?.setAudioConstraints({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      }).catch((err) => console.warn(`${LOG} setAudioConstraints failed`, err));

      dev.on("incoming", (call: Call) => {
        if (destroyed || device !== dev) {
          try { call.reject(); } catch { /* noop */ }
          return;
        }
        breadcrumb("Incoming call delivered", {
          from: call.customParameters?.get("fromE164"),
          sessionName: call.customParameters?.get("sessionName"),
        });
        onIncoming(call, dev);
      });
      dev.on("registered", () => {
        attempt = 0;
        breadcrumb("Receiver registered", { identity });
      });
      dev.on("unregistered", () => {
        // A live call keeps the device alive; the provider's post-call
        // recycle() rebuilds it, so a mid-call retry would only fight it.
        if (device === dev && !dev.isBusy) scheduleRetry("unregistered");
      });
      dev.on("error", (err: { message?: string; code?: number }) => {
        Sentry.captureException(
          err instanceof Error ? err : new Error(`${LOG} ${err?.message ?? "Unknown device error"}`),
          { tags: { component: "TwilioReceiver", source: "Device.on(error)" }, extra: { twilioCode: err?.code } },
        );
        if (device === dev && !dev.isBusy) scheduleRetry("device-error");
      });
      dev.on("tokenWillExpire", async () => {
        try {
          const fresh = await fetchAgentVoiceToken();
          if (device === dev) dev.updateToken(fresh.token);
        } catch (e) {
          // The expiring token will drop registration; the unregistered
          // handler rebuilds with a brand-new token.
          Sentry.captureException(e, { tags: { component: "TwilioReceiver", operation: "tokenRefresh" } });
        }
      });

      await dev.register();
      if (destroyed || gen !== generation) {
        try { dev.destroy(); } catch { /* noop */ }
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { component: "TwilioReceiver", operation: "start" } });
      if (!destroyed && gen === generation) scheduleRetry("start-failed");
    }
  };

  // Connectivity came back: skip whatever backoff is pending and re-register
  // now — until then the agent silently misses every inbound ring.
  const onOnline = () => {
    if (destroyed || retryTimer === null) return;
    breadcrumb("Back online — re-registering receiver now");
    clearRetry();
    void start();
  };
  window.addEventListener("online", onOnline);

  void start();

  return {
    recycle: () => {
      if (destroyed) return;
      breadcrumb("Receiver recycle requested");
      // Deferred: recycle() is called from finishCall, often inside one of
      // this very device's event dispatches (same pattern as the outbound
      // Device's deferred teardown).
      window.setTimeout(() => {
        if (!destroyed) void start();
      }, 0);
    },
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      breadcrumb("Receiver destroyed");
      window.removeEventListener("online", onOnline);
      clearRetry();
      teardownDevice();
    },
  };
}
